import { createI18n } from "vue-i18n";
import { getCache } from "@/utils/cache";
import app from "@/constants/app";
import elEn from "element-plus/lib/locale/lang/en";
import elDe from "element-plus/lib/locale/lang/de";
import elFr from "element-plus/lib/locale/lang/fr";
import elEs from "element-plus/lib/locale/lang/es";
import elIt from "element-plus/lib/locale/lang/it";
// // 本地语言包
import en from "./en"; //英语
import de from "./de"; //德语
import fr from "./fr"; //法语
import es from "./es"; //西班牙语
import it from "./it"; //意大利语

export const supportLangs = {
  en_US: {
    langName: "English",
    el: elEn,
    ...en,
  },
  de: {
    langName: "Deutsch",
    el: elDe,
    ...de,
  },
  fr: {
    langName: "Français",
    el: elFr,
    ...fr,
  },
  es: {
    langName: "Español",
    el: elEs,
    ...es,
  },
  it: {
    langName: "Italiano",
    el: elIt,
    ...it,
  },
};

/** 与 supportLangs 一致的 locale key 列表 */
export const SUPPORTED_LOCALE_KEYS = Object.keys(supportLangs);

const isSupportedLocale = (code) =>
  Boolean(code) && SUPPORTED_LOCALE_KEYS.includes(code);

/**
 * 将历史缓存、BCP 47 标签等规范为 supportLangs 的 key（不在列表内则返回 null）
 */
export const normalizeLocaleInput = (raw) => {
  if (raw == null || typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (isSupportedLocale(trimmed)) return trimmed;
  const lower = trimmed.toLowerCase();
  const primary = lower.split(/[-_]/)[0];
  if (primary === "en") return "en_US";
  if (["de", "fr", "es", "it"].includes(primary)) return primary;
  return null;
};

/** 1. 本地已保存的用户语言（LocalStorage，与 setCache 前缀一致） */
const getCachedUserChoice = () => {
  const raw = getCache("CacheLang", { isParse: false }, null);
  return normalizeLocaleInput(raw);
};

/** 2. 浏览器 navigator.language / navigator.languages */
const resolveFromNavigator = () => {
  if (typeof navigator === "undefined") return null;
  const seen = new Set();
  const tags = [];
  if (navigator.languages?.length) {
    for (const t of navigator.languages) {
      if (t && !seen.has(t)) {
        seen.add(t);
        tags.push(t);
      }
    }
  }
  if (navigator.language && !seen.has(navigator.language)) {
    tags.unshift(navigator.language);
  }
  for (const tag of tags) {
    const norm = normalizeLocaleInput(tag);
    if (norm && isSupportedLocale(norm)) return norm;
  }
  return null;
};

/**
 * 3. ISO 3166-1 alpha-2 → 默认 UI 语言（仅 de/fr/es/it/en_US；未列出国家 → 英语）
 * 刻意保守：多官方语国家未强绑单一语种，避免误判。
 */
const COUNTRY_TO_LOCALE = {
  // 德语区
  DE: "de",
  AT: "de",
  LI: "de",
  // 法语区（不含 BE/CH 等强多语国家，默认走英语）
  FR: "fr",
  MC: "fr",
  LU: "fr",
  // 西班牙语区（西班牙 + 主要拉美西语国家）
  ES: "es",
  MX: "es",
  AR: "es",
  CO: "es",
  CL: "es",
  PE: "es",
  VE: "es",
  EC: "es",
  GT: "es",
  CU: "es",
  BO: "es",
  DO: "es",
  HN: "es",
  PY: "es",
  SV: "es",
  NI: "es",
  CR: "es",
  PA: "es",
  UY: "es",
  PR: "es",
  // 意大利语区
  IT: "it",
  SM: "it",
  VA: "it",
};

const localeFromCountryCode = (iso2) => {
  if (!iso2 || typeof iso2 !== "string") return app.defaultLang;
  const code = iso2.trim().toUpperCase();
  const mapped = COUNTRY_TO_LOCALE[code];
  if (mapped && isSupportedLocale(mapped)) return mapped;
  return app.defaultLang;
};

/**
 * 同步解析初始语言：缓存 → 浏览器 → 英语（IP 在 initGeoLocaleFromIp 中异步补充）
 */
const resolveInitialLocaleSync = () => {
  const cached = getCachedUserChoice();
  if (cached) return { locale: cached, geoFromIpNeeded: false };
  const fromNav = resolveFromNavigator();
  if (fromNav) return { locale: fromNav, geoFromIpNeeded: false };
  return { locale: app.defaultLang, geoFromIpNeeded: true };
};

const { locale: initialLocale, geoFromIpNeeded } = resolveInitialLocaleSync();

let geoFromIpNeededFlag = geoFromIpNeeded;

const readI18nLocaleString = (i18nInstance) => {
  const loc = i18nInstance?.global?.locale;
  if (loc == null) return "";
  return typeof loc === "object" && "value" in loc ? loc.value : String(loc);
};

const writeI18nLocale = (i18nInstance, value) => {
  const loc = i18nInstance?.global?.locale;
  if (loc != null && typeof loc === "object" && "value" in loc) {
    loc.value = value;
  } else if (i18nInstance?.global) {
    i18nInstance.global.locale = value;
  }
};

// 定义消息（历史结构保留；实际 messages 使用 supportLangs）
const messages = {
  en,
  de,
  fr,
  es,
  it,
};
/**
 * 取语言名称
 * @param lang
 * @returns
 */
export const getLangName = (lang) => {
  return supportLangs[lang]?.langName;
};

const i18n = createI18n({
  legacy: false,
  locale: initialLocale,
  fallbackLocale: app.defaultLang,
  messages: supportLangs,
});

/**
 * 当前界面语言（与 i18n 一致；IP 异步解析完成后 axios 等也会拿到新值）
 */
export const getLocaleLang = () => readI18nLocaleString(i18n) || app.defaultLang;

/**
 * 无本地偏好且浏览器未命中时，根据 IP 国家更新语言（不写入 CacheLang，避免覆盖用户显式选择）
 * 在应用启动后调用一次即可。
 */
export const initGeoLocaleFromIp = async () => {
  if (!geoFromIpNeededFlag) return;
  geoFromIpNeededFlag = false;

  let countryCode = null;
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 6000);
    const res = await fetch("https://ipwho.is/", {
      signal: controller.signal,
    });
    clearTimeout(timer);
    if (!res.ok) return;
    const data = await res.json();
    if (data && data.success !== false && data.country_code) {
      countryCode = String(data.country_code).trim();
    }
  } catch {
    return;
  }

  if (!countryCode) return;
  // 用户已在等待期间通过语言切换写入缓存
  if (getCachedUserChoice()) return;
  // 用户或别处在等待期间已改界面语言
  if (readI18nLocaleString(i18n) !== app.defaultLang) return;

  const next = localeFromCountryCode(countryCode);
  if (next && next !== readI18nLocaleString(i18n)) {
    writeI18nLocale(i18n, next);
  }
};

export default i18n;
